import React, { useEffect, useMemo, useState, memo, Suspense, lazy } from 'react';
import { 
  Loader2, ShoppingCart, LayoutDashboard, PackageSearch, Wallet, 
  Fingerprint, Users, Fuel, Contact2, BarChart3, MessageSquare, 
  CalendarDays, ClipboardList, Globe, ShieldCheck, KeyRound, Menu, X, LogOut, Sun, Moon, ChevronLeft, ChevronRight, Settings
  , Building2
} from 'lucide-react';
import { useMenuData } from '../hooks/useMenuData';

// Lazy loading with retry mechanism
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const hasRetried = JSON.parse(
      window.sessionStorage.getItem(`retry-${componentImport.toString()}`) || 'false'
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem(`retry-${componentImport.toString()}`, 'false');
      return component;
    } catch (error) {
      if (!hasRetried) {
        window.sessionStorage.setItem(`retry-${componentImport.toString()}`, 'true');
        return window.location.reload();
      }
      throw error;
    }
  });

const Sale = lazyWithRetry(() => import('../modules/sale'));
const MenuManager = lazyWithRetry(() => import('../modules/MenuManager'));
const Monitoring = lazyWithRetry(() => import('../modules/Monitoring'));
const ManagerDashboard = lazyWithRetry(() => import('../modules/ManagerDashboard'));
const Rider = lazyWithRetry(() => import('../modules/Rider'));
const Expense = lazyWithRetry(() => import('../modules/expense'));
const Staff = lazyWithRetry(() => import('../modules/Staff'));
const Report = lazyWithRetry(() => import('../modules/report'));
const DailySalesCalendar = lazyWithRetry(() => import('../modules/SaleCalender'));
const ExpenseCalendar = lazyWithRetry(() => import('../modules/ExpenseCalender'));
const Customers = lazyWithRetry(() => import('../modules/Customers'));
const Inventory = lazyWithRetry(() => import('../modules/Inventory'));
const Reviews = lazyWithRetry(() => import('../modules/Reviews'));
const WebOrdersManager = lazyWithRetry(() => import('../modules/WebOrdersManager'));
const AccountSecurity = lazyWithRetry(() => import('../modules/AccountSecurity'));
const RestaurantSettings = lazyWithRetry(() => import('../modules/RestaurantSettings'));
const SecuritySettings = lazyWithRetry(() => import('../modules/SecuritySettings'));

const NAV_MODES = [
  { id: 'sale', label: 'Sale', icon: ShoppingCart },
  { id: 'menu-manager', label: 'Menu Catalog', icon: ClipboardList },
  { id: 'monitoring', label: 'Monitoring', icon: LayoutDashboard },
  { id: 'inventory', label: 'Inventory Intelligence', icon: PackageSearch },
  { id: 'expense', label: 'Expense', icon: Wallet },
  { id: 'attendance', label: 'Manager Dashboard', icon: Fingerprint },
  { id: 'staff', label: 'Staff Management', icon: Users },
  { id: 'fuel', label: 'Rider & Fuel', icon: Fuel },
  { id: 'customers', label: 'Customers', icon: Contact2 },
  { id: 'report', label: 'Reports', icon: BarChart3 },
  { id: 'reviews', label: 'Customer Reviews', icon: MessageSquare },
  { id: 'sale log', label: 'Sale Log', icon: CalendarDays },
  { id: 'expense log', label: 'Expense Log', icon: ClipboardList },
  { id: 'web-orders', label: 'Web Orders', icon: Globe },
  { id: 'restaurant-settings', label: 'Restaurant Settings', icon: Settings },
  { id: 'account-security', label: 'Account & Security', icon: KeyRound },
  { id: 'security', label: 'SaaS Security Center', icon: ShieldCheck }
];

const ROLE_ALIASES = {
  'super admin': 'super_admin',
  superadmin: 'super_admin',
};

const FULL_ACCESS_ROLES = new Set(['owner', 'admin', 'super_admin']);

const normalizeRole = (role) => {
  const cleaned = String(role || 'owner')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  return ROLE_ALIASES[cleaned] || cleaned.replace(/\s+/g, '_');
};

const ROLE_NAV_ACCESS = {
  manager: [
    'sale',
    'menu-manager',
    'monitoring',
    'inventory',
    'expense',
    'attendance',
    'staff',
    'fuel',
    'customers',
    'report',
    'reviews',
    'sale log',
    'expense log',
    'web-orders',
    'restaurant-settings',
    'account-security'
  ],
  cashier: ['sale', 'sale log', 'account-security'],
  order_taker: ['sale', 'account-security'],
  kitchen: ['monitoring', 'menu-manager'],
  chef: ['monitoring', 'menu-manager'],
  rider: ['fuel', 'account-security'],
  staff: ['attendance', 'account-security']
};

const getAllowedNavModes = (role) => {
  const normalizedRole = normalizeRole(role);
  if (FULL_ACCESS_ROLES.has(normalizedRole)) return NAV_MODES;

  const allowedIds = ROLE_NAV_ACCESS[normalizedRole] || ['sale'];
  return NAV_MODES.filter(item => allowedIds.includes(item.id));
};

const LoadingFallback = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <Loader2 className="animate-spin text-[#F25C05]" size={48} />
  </div>
);

const AdminPanel = memo(({ isDark, setIsDark, handleLogout, currentRestaurantId, restaurantMemberships = [], onRestaurantSwitch }) => {
  const [mode, setMode] = useState('sale');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentRestaurant = restaurantMemberships.find(r => r.restaurant_id === currentRestaurantId);
  const currentRole = normalizeRole(currentRestaurant?.role);
  const allowedNavModes = useMemo(() => getAllowedNavModes(currentRole), [currentRole]);

  useEffect(() => {
    if (!allowedNavModes.some(item => item.id === mode)) {
      setMode(allowedNavModes[0]?.id || 'sale');
    }
  }, [allowedNavModes, mode]);

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#0D0D0D] text-[#e5e2e1]' : 'bg-gray-50 text-slate-900'} font-space flex`}>
      
      {/* --- 🧭 COLLAPSIBLE SIDEBAR --- */}
      <aside className={`print:hidden fixed h-screen z-[60] bg-[#111] border-r border-[#333] transition-all duration-500 ease-in-out group hidden xl:flex flex-col ${isSidebarCollapsed ? 'w-20' : 'w-72'}`}>
        
        {/* Brand / Logo */}
        <div className="p-6 mb-4 flex items-center justify-between">
           {!isSidebarCollapsed ? (
             <div className="flex items-center gap-3 animate-in fade-in zoom-in duration-500">
               <div className="w-10 h-10 bg-gradient-to-br from-[#F25C05] to-[#f7600c] rounded-xl flex items-center justify-center shadow-lg shadow-[#F25C05]/20">
                 <ShoppingCart size={20} className="text-white" />
               </div>
               <div className="flex flex-col">
                 <span className="text-xl font-black tracking-tighter text-white italic leading-none">Pizza<span className="text-[#F25C05]">Alif</span></span>
                 <span className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mt-1">Management ERP</span>
               </div>
             </div>
           ) : (
             <div className="w-12 h-12 bg-gradient-to-br from-[#F25C05] to-[#f7600c] rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-[#F25C05]/20">
                <ShoppingCart size={24} className="text-white" />
             </div>
           )}
        </div>

        {!isSidebarCollapsed && (
          <div className="mx-3 mb-3 rounded-2xl border border-[#252525] bg-[#0d0d0d] p-3">
            <div className="mb-2 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-gray-500">
              <Building2 size={14} className="text-[#F25C05]" />
              Restaurant
            </div>
            <select
              value={currentRestaurantId || ''}
              onChange={(e) => onRestaurantSwitch?.(e.target.value)}
              disabled={restaurantMemberships.length <= 1}
              className="w-full rounded-xl border border-[#333] bg-[#050505] px-3 py-3 text-[11px] font-black uppercase tracking-wider text-white outline-none transition-all focus:border-[#F25C05] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {restaurantMemberships.length === 0 ? (
                <option value={currentRestaurantId || ''}>{currentRestaurant?.name || 'Current Restaurant'}</option>
              ) : (
                restaurantMemberships.map(restaurant => (
                  <option key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                    {restaurant.name} ({restaurant.role})
                  </option>
                ))
              )}
            </select>
            <p className="mt-2 truncate text-[8px] font-bold uppercase tracking-widest text-gray-600">
              {currentRestaurant?.role || 'member'} access
            </p>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto custom-scrollbar">
           {allowedNavModes.map(item => (
             <button
                key={item.id}
                onClick={() => setMode(item.id)}
                title={item.label}
                className={`w-full flex items-center transition-all duration-300 relative group/item rounded-xl ${
                  isSidebarCollapsed ? 'justify-center py-4' : 'gap-4 px-4 py-3.5'
                } ${
                  mode === item.id 
                  ? 'bg-[#F25C05] text-white shadow-xl shadow-[#F25C05]/30' 
                  : 'text-gray-500 hover:bg-white/5 hover:text-gray-300'
                }`}
             >
                {/* Active Indicator Bar */}
                {mode === item.id && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full animate-in slide-in-from-left-full duration-300" />
                )}

                <item.icon size={20} className={`shrink-0 ${mode === item.id ? 'scale-110' : 'group-hover/item:scale-110'} transition-transform`} />
                
                {!isSidebarCollapsed && (
                  <span className="text-[11px] font-black uppercase tracking-widest whitespace-nowrap animate-in slide-in-from-left-2 duration-300">
                    {item.label}
                  </span>
                )}

                {isSidebarCollapsed && (
                  <div className="absolute left-full ml-4 px-4 py-2 bg-[#1A1A1A] border border-[#333] text-white text-[10px] font-black uppercase tracking-widest rounded-lg opacity-0 group-hover/item:opacity-100 translate-x-4 group-hover/item:translate-x-0 transition-all pointer-events-none shadow-2xl z-[100] whitespace-nowrap">
                    {item.label}
                  </div>
                )}
             </button>
           ))}
        </nav>

        {/* Footer Actions (Theme/Logout) */}
        <div className="p-3 border-t border-[#333] space-y-1.5">
           <button 
             onClick={() => setIsDark(!isDark)}
             title="Switch Theme"
             className={`w-full flex items-center rounded-xl text-gray-500 hover:bg-white/5 hover:text-white transition-all ${
               isSidebarCollapsed ? 'justify-center py-4' : 'gap-4 px-4 py-3.5'
             }`}
           >
              {isDark ? <Sun size={20}/> : <Moon size={20}/>}
              {!isSidebarCollapsed && <span className="text-[11px] font-black uppercase tracking-widest">Theme Mode</span>}
           </button>
           <button 
             onClick={handleLogout}
             title="Sign Out"
             className={`w-full flex items-center rounded-xl text-red-500 hover:bg-red-500/10 transition-all ${
               isSidebarCollapsed ? 'justify-center py-4' : 'gap-4 px-4 py-3.5'
             }`}
           >
              <LogOut size={20}/>
              {!isSidebarCollapsed && <span className="text-[11px] font-black uppercase tracking-widest">Sign Out</span>}
           </button>
        </div>

        {/* Collapse Toggle Button */}
        <button 
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          className="absolute top-1/2 -right-3 w-6 h-6 bg-[#333] border border-[#444] rounded-full flex items-center justify-center text-white hover:bg-[#F25C05] transition-all z-[70] shadow-xl"
        >
          {isSidebarCollapsed ? <ChevronRight size={14}/> : <ChevronLeft size={14}/>}
        </button>
      </aside>

      {/* --- 📱 MOBILE NAVIGATION BAR --- */}
      <header className="xl:hidden fixed top-0 left-0 right-0 h-16 bg-[#111] border-b border-[#333] px-6 flex items-center justify-between z-[60]">
         <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#F25C05] rounded-xl flex items-center justify-center shadow-lg">
              <ShoppingCart size={18} className="text-white" />
            </div>
            <span className="text-xl font-black tracking-tighter text-white italic">Pizza<span className="text-[#F25C05]">Alif</span></span>
         </div>
         <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-gray-400 hover:text-white"
         >
            {isMobileMenuOpen ? <X size={24}/> : <Menu size={24}/>}
         </button>
      </header>

      {/* Mobile Menu Content */}
      {isMobileMenuOpen && (
        <div className="xl:hidden fixed inset-0 z-[55] bg-[#0D0D0D]/95 backdrop-blur-xl pt-20 px-6 overflow-y-auto animate-in fade-in duration-300">
           <div className="space-y-2 pb-10">
              <div className="mb-4 rounded-2xl border border-[#252525] bg-[#111] p-4">
                <div className="mb-2 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-gray-500">
                  <Building2 size={14} className="text-[#F25C05]" />
                  Restaurant
                </div>
                <select
                  value={currentRestaurantId || ''}
                  onChange={(e) => onRestaurantSwitch?.(e.target.value)}
                  disabled={restaurantMemberships.length <= 1}
                  className="w-full rounded-xl border border-[#333] bg-[#050505] px-3 py-3 text-[11px] font-black uppercase tracking-wider text-white outline-none focus:border-[#F25C05] disabled:opacity-70"
                >
                  {restaurantMemberships.length === 0 ? (
                    <option value={currentRestaurantId || ''}>{currentRestaurant?.name || 'Current Restaurant'}</option>
                  ) : (
                    restaurantMemberships.map(restaurant => (
                      <option key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                        {restaurant.name} ({restaurant.role})
                      </option>
                    ))
                  )}
                </select>
              </div>
              {allowedNavModes.map(item => (
                <button
                   key={item.id}
                   onClick={() => { setMode(item.id); setIsMobileMenuOpen(false); }}
                   className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all ${
                     mode === item.id ? 'bg-[#F25C05] text-white' : 'text-gray-500 bg-[#1A1A1A]'
                   }`}
                >
                   <item.icon size={20} />
                   <span className="text-xs font-black uppercase tracking-widest">{item.label}</span>
                </button>
              ))}
              <div className="pt-4 border-t border-[#333] space-y-2">
                 <button onClick={() => { setIsDark(!isDark); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-4 px-6 py-4 rounded-2xl text-gray-500 bg-[#1A1A1A]">
                    {isDark ? <Sun size={20}/> : <Moon size={20}/>}
                    <span className="text-xs font-black uppercase tracking-widest">Switch Theme</span>
                 </button>
                 <button onClick={handleLogout} className="w-full flex items-center gap-4 px-6 py-4 rounded-2xl text-red-500 bg-red-500/10">
                    <LogOut size={20}/>
                    <span className="text-xs font-black uppercase tracking-widest">Sign Out</span>
                 </button>
              </div>
           </div>
        </div>
      )}

      {/* --- 🖥️ MAIN CONTENT AREA --- */}
      <main className={`w-full min-w-0 overflow-x-hidden transition-all duration-500 min-h-screen pt-20 xl:pt-0 ${isSidebarCollapsed ? 'xl:pl-20' : 'xl:pl-72'}`}>
        <div className="w-full max-w-[1750px] mx-auto p-4 md:p-8 min-w-0">
          <Suspense fallback={<LoadingFallback />}>
            {mode === 'sale' && <Sale isDark={isDark} currentRole={currentRole} currentRestaurantId={currentRestaurantId} />}
            {mode === 'menu-manager' && <MenuManagerWrapper isDark={isDark} />}
            {mode === 'monitoring' && <Monitoring isDark={isDark} />}
            {mode === 'inventory' && <Inventory isDark={isDark} />}
            {mode === 'attendance' && <ManagerDashboard isDark={isDark} />}
            {mode === 'staff' && <Staff isDark={isDark} />}
            {mode === 'expense' && <Expense isDark={isDark} />}
            {mode === 'fuel' && <Rider isDark={isDark} />}
            {mode === 'customers' && <Customers isDark={isDark} />}
            {mode === 'reviews' && <Reviews isDark={isDark} />}
            {mode === 'report' && <Report isDark={isDark} />}
            {mode === 'sale log' && <DailySalesCalendar isDark={isDark} />}
            {mode === 'expense log' && <ExpenseCalendar isDark={isDark} />}
            {mode === 'web-orders' && <WebOrdersManager isDark={isDark} />}
            {mode === 'restaurant-settings' && <RestaurantSettings isDark={isDark} />}
            {mode === 'account-security' && <AccountSecurity isDark={isDark} />}
            {mode === 'security' && (
              <SecuritySettings
                isDark={isDark}
                currentRestaurantId={currentRestaurantId}
                restaurantMemberships={restaurantMemberships}
              />
            )}
          </Suspense>
        </div>
      </main>
    </div>
  );
});

const MenuManagerWrapper = ({ isDark }) => {
  const menuData = useMenuData();
  return <MenuManager menuData={menuData} isDark={isDark} />;
};

export default AdminPanel;
